import { useNavigate } from "react-router-dom";
import {
  contactMergeDestination,
  contactSaveError,
  isContactReviewPending,
} from "@/modules/common/utils/contact-merge";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { queryClient } from "@/queryClient";
import { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  addCustomerTelephone,
  deleteCustomerTelephone,
  updateCustomerTelephone,
} from "@/modules/lead-page/api/lead-page.v2.api";
import { UUID } from "crypto";
import { AddTelephone, UpdateTelephone } from "@/modules/common/dtos/index.v2";
import { normalizeContactPhone } from "@/modules/common/utils/customer-contact";
import { invalidateCustomerContactQueries } from "@/modules/common/utils/customer-contact-cache";

const useEditTelephone = () => {
  const navigate = useNavigate();
  const { lead } = useContext(LeadPageContext);
  const [editTelephoneModalOpen, setEditTelephoneModalOpen] = useState(false);
  const [telephoneIdToDelete, setTelephoneIdToDelete] = useState<UUID | null>(
    null,
  );
  const [telephoneIdToMakeDefault, setTelephoneIdToMakeDefault] =
    useState<UUID | null>(null);
  const [telephone, setTelephone] = useState("");
  const [addTelephoneValidationError, setAddTelephoneValidationError] =
    useState("");

  const {
    mutate: makeTelephonePrimaryMutation,
    isLoading: isLoadingMakeTelephonePrimary,
    isError: isErrorMakeTelephonePrimary,
    error: errorMakeTelephonePrimary,
  } = useMutation({
    mutationFn: ({
      telephoneID,
      update,
    }: {
      telephoneID: UUID;
      update: UpdateTelephone;
    }) => updateCustomerTelephone(telephoneID, update),
    onSuccess: () => {
      void invalidateCustomerContactQueries(queryClient);
      showToast({
        type: ToastType.SUCCESS,
        message: "Phone Number set as Main",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },

    onError: () => {
      showToast({
        type: ToastType.ERROR,
        message: "Failed to set phone number as Main",
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const {
    mutateAsync: deleteCustomerTelephoneMutation,
    isLoading: isDeleteCustomerTelephoneLoading,
    isError: isDeleteCustomerTelephoneError,
  } = useMutation({
    mutationFn: ({ telephoneUID }: { telephoneUID: UUID }) =>
      deleteCustomerTelephone(telephoneUID),
    onSuccess: () => {
      void invalidateCustomerContactQueries(queryClient);
      showToast({
        type: ToastType.SUCCESS,
        message: "Phone Number deleted",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      setTelephone("");
    },
    onError: () => {
      showToast({
        type: ToastType.ERROR,
        message: "Failed to delete phone number",
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const {
    mutate: addCustomerTelephoneMutation,
    isLoading: isLoadingAddTelephone,
    isError: isErrorAddTelephone,
  } = useMutation({
    mutationFn: ({
      customerId,
      update,
    }: {
      customerId: UUID;
      update: AddTelephone;
    }) => addCustomerTelephone(customerId, update),
    onSuccess: (response) => {
      const destination = contactMergeDestination(response);
      if (destination) navigate(destination, { replace: true });
      setEditTelephoneModalOpen(false);
      void invalidateCustomerContactQueries(queryClient);
      showToast({
        type: ToastType.SUCCESS,
        message: response.mergeOutcome?.merged
          ? "Phone added and duplicate leads merged"
          : "Phone Number added",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      setTelephone("");
    },
    onError: (error: any) => {
      if (isContactReviewPending(error))
        void queryClient.invalidateQueries({ queryKey: ["contact-conflicts"] });
      showToast({
        type: ToastType.ERROR,
        message: contactSaveError(error),
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const handleMakeTelephonePrimary = (telephoneId: UUID) => {
    if (lead?.data?.lead?.customer_uid) {
      makeTelephonePrimaryMutation({
        telephoneID: telephoneId,
        update: { default: true },
      });
    }
  };

  const handleDeleteTelephone = (telephoneId: UUID) => {
    deleteCustomerTelephoneMutation({
      telephoneUID: telephoneId,
    });
  };

  const handleAddTelephone = () => {
    if (lead?.data?.lead?.customer_uid && normalizeContactPhone(telephone)) {
      addCustomerTelephoneMutation({
        customerId: lead?.data?.lead?.customer_uid,
        update: {
          telephone: telephone,
          default: false,
          originJourneyUId: lead.data.lead.journey_uid,
        },
      });
    }
  };

  const handleChangeTelephone = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!normalizeContactPhone(e.target.value))
      setAddTelephoneValidationError("Please enter a correct number");
    else setAddTelephoneValidationError("");
    setTelephone(e.target.value);
  };

  return {
    telephone: {
      editTelephoneModalOpen,
      setEditTelephoneModalOpen,
      handleChangeTelephone,
      addTelephone: {
        handleAddTelephone,
        isLoadingAddTelephone,
        isAddingTelephone: isLoadingAddTelephone,
        isErrorAddTelephone,
        addTelephoneValidationError,
      },
      deleteTelephone: {
        handleDeleteTelephone,
        isLoadingDeleteTelephone: isDeleteCustomerTelephoneLoading,
        isErrorDeleteTelephone: isDeleteCustomerTelephoneError,
        telephoneIdToDelete,
        isDeletingTelephone: isDeleteCustomerTelephoneLoading,
        setTelephoneIdToDelete,
      },
      makeTelephonePrimary: {
        handleMakeTelephonePrimary,
        isMakingTelephonePrimary: isLoadingMakeTelephonePrimary,
        setTelephoneIdToMakeDefault,
        telephoneIdToMakeDefault,
        isErrorMakeTelephonePrimary,
        errorMakeTelephonePrimary,
      },
      value: lead?.data?.lead?.lead_telephones || "",
      telephoneList: lead?.data?.lead?.lead_telephones?.slice().sort((a, b) => {
        if (a.primary && !b.primary) return -1;
        if (!a.primary && b.primary) return 1;
        return 0;
      }),
      state: telephone,
      setTelephone,
      isMakingTelephonePrimary: isLoadingMakeTelephonePrimary,
    },
  };
};

export default useEditTelephone;
